import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  ClickAwayListener,
  TextField,
  Typography,
} from "@mui/material";
import {
  PendenciasIcon,
  PerformanceIcon,
  ExecutivoIcon,
  SearchCpfIcon,
} from "./insightsIcons";
import { CONDUCCO_PRIMARY, INSIGHTS_FONT } from "./tokens";
import { parseCpfList } from "./parseCpfList";
import { downloadHtmlReport } from "./downloadHtmlReport";
import {
  generateInsightsReport,
  getInsightsErrorMessage,
  InsightsReportType,
} from "v2/services/insights.service";
import { useAlert } from "contexts/alert";

interface ReportButtonConfig {
  type: InsightsReportType;
  label: string;
  Icon: typeof PendenciasIcon;
}

// DT_MOVIMENTACOES_ESTADO tem bug de reconciliação (pendências fantasma
// inflam contagens) — Pendências/Performance/Executivo saem do ar até o DAG
// ser corrigido. Timeline CPF não é afetado (fonte é DT_TIMELINE, append-only).
const REPORTS_ENABLED = false;

const REPORT_BUTTONS: ReportButtonConfig[] = [
  { type: "pendencias", label: "Pendências", Icon: PendenciasIcon },
  { type: "performance", label: "Performance", Icon: PerformanceIcon },
  { type: "executivo", label: "Executivo", Icon: ExecutivoIcon },
];

const REPORT_BUTTON_SX = {
  fontFamily: INSIGHTS_FONT,
  textTransform: "none" as const,
  fontWeight: 600,
  color: CONDUCCO_PRIMARY,
  borderColor: CONDUCCO_PRIMARY,
  "&:hover": {
    borderColor: CONDUCCO_PRIMARY,
    backgroundColor: "rgba(48,36,225,0.08)",
  },
};

interface ReportButtonsProps {
  workspaceId: string;
  disabled?: boolean;
  onError: (message: string) => void;
}

export function ReportButtons({ workspaceId, disabled, onError }: ReportButtonsProps) {
  const [generatingType, setGeneratingType] = useState<InsightsReportType | null>(null);
  const [showCpfInput, setShowCpfInput] = useState(false);
  const [cpfInput, setCpfInput] = useState("");
  const [cpfError, setCpfError] = useState<string | null>(null);
  const { openAlert } = useAlert();

  const allDisabled = disabled || generatingType !== null;

  const closeCpfInput = () => {
    setShowCpfInput(false);
    setCpfInput("");
    setCpfError(null);
  };

  const runReport = async (type: InsightsReportType, cpfs?: string[]) => {
    setGeneratingType(type);
    try {
      const response = await generateInsightsReport(type, {
        workspace_id: workspaceId,
        cpfs,
      });
      await downloadHtmlReport(response.html, response.filename);
      openAlert("Relatório baixado", "success");
      if (type === "timeline") closeCpfInput();
    } catch (error: any) {
      onError(getInsightsErrorMessage(error, "Serviço indisponível, tente novamente"));
    } finally {
      setGeneratingType(null);
    }
  };

  const handleTimelineConfirm = () => {
    const result = parseCpfList(cpfInput);
    if (result.error) {
      setCpfError(result.error);
      return;
    }
    runReport("timeline", result.cpfs);
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1,
        py: 1.5,
        px: 2,
      }}
    >
      {REPORTS_ENABLED &&
        REPORT_BUTTONS.map(({ type, label, Icon }) => (
          <Button
            key={type}
            variant="outlined"
            size="small"
            disabled={allDisabled}
            onClick={() => runReport(type)}
            startIcon={
              generatingType === type ? (
                <CircularProgress size={16} sx={{ color: CONDUCCO_PRIMARY }} />
              ) : (
                <Icon fontSize="small" />
              )
            }
            sx={REPORT_BUTTON_SX}
          >
            {label}
          </Button>
        ))}

      <Box sx={{ position: "relative" }}>
        <Button
          variant="outlined"
          size="small"
          disabled={allDisabled}
          onClick={() => setShowCpfInput((prev) => !prev)}
          startIcon={
            generatingType === "timeline" ? (
              <CircularProgress size={16} sx={{ color: CONDUCCO_PRIMARY }} />
            ) : (
              <SearchCpfIcon fontSize="small" />
            )
          }
          sx={REPORT_BUTTON_SX}
        >
          Timeline CPF
        </Button>

        {showCpfInput && (
          <ClickAwayListener onClickAway={closeCpfInput}>
            <Box
              sx={{
                position: "absolute",
                top: "calc(100% + 8px)",
                left: 0,
                zIndex: 10,
                backgroundColor: "#fff",
                border: `1px solid ${CONDUCCO_PRIMARY}`,
                borderRadius: 2,
                p: 1.5,
                boxShadow: "0 4px 14px rgba(48,36,225,0.18)",
                display: "flex",
                flexDirection: "column",
                gap: 1,
                minWidth: 280,
              }}
            >
              <TextField
                size="small"
                placeholder="CPFs separados por vírgula"
                value={cpfInput}
                onChange={(e) => {
                  setCpfInput(e.target.value);
                  setCpfError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") closeCpfInput();
                }}
                autoFocus
                error={Boolean(cpfError)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&.Mui-focused fieldset": {
                      borderColor: CONDUCCO_PRIMARY,
                      boxShadow: "0 0 0 3px rgba(48,36,225,0.15)",
                    },
                  },
                }}
              />
              {cpfError && (
                <Typography variant="caption" color="error" sx={{ fontFamily: INSIGHTS_FONT }}>
                  {cpfError}
                </Typography>
              )}
              <Button
                variant="contained"
                size="small"
                disabled={!cpfInput.trim() || allDisabled}
                onClick={handleTimelineConfirm}
                sx={{
                  alignSelf: "flex-end",
                  backgroundColor: CONDUCCO_PRIMARY,
                  fontFamily: INSIGHTS_FONT,
                  textTransform: "none",
                  "&:hover": { backgroundColor: CONDUCCO_PRIMARY, opacity: 0.9 },
                }}
              >
                Gerar
              </Button>
            </Box>
          </ClickAwayListener>
        )}
      </Box>
    </Box>
  );
}
